import Header from "../components/Header";
import Empty from "../components/Empty";
import Error from "../components/Error";
import Meta from "../../../components/Meta";
import Data from "../../../../repository";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const Field = ({ label, value }) => (
    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
        <p>{label}</p>
        <p className={"text-textGrey col-span-2"}>{value === undefined || value === null || value === '' ? '--' : value}</p>
    </div>
);

const formatBalance = (value) => `£${(Number(value || 0) / 100).toFixed(2)}`;

const TX_TYPE_LABELS = {
    purchase: 'Top Up',
    spend: 'Spend',
    refund: 'Refund',
    adjustment: 'Adjustment',
    membership_grant: 'Membership Grant',
};

const formatTxDate = (date) => {
    if (!date) return '--';
    return new Date(date).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
};

const WalletHistory = ({ userId }) => {
    const [transactions, setTransactions] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const result = await Data.adminFetchUserWalletTransactions(userId);
            setTransactions(result.data || []);
            setLoaded(true);
        } finally {
            setLoading(false);
        }
    };

    if (!loaded) {
        return (
            <button
                type={"button"}
                onClick={load}
                disabled={loading}
                className={"bg-backgroundGrey px-3 py-1.5 rounded-md hover:opacity-80 transition disabled:opacity-60 disabled:cursor-not-allowed text-sm"}
            >
                {loading ? 'Loading...' : 'View Wallet History'}
            </button>
        );
    }

    if (!transactions.length) return <p className={"text-textGrey text-sm"}>No wallet transactions found.</p>;

    return (
        <div className={"overflow-x-auto"} style={{ WebkitOverflowScrolling: 'touch' }}>
            <table className={"min-w-[560px] w-full border-collapse"}>
                <thead>
                <tr className={"text-left"}>
                    <th className={"px-2 py-2 text-sm text-textGrey"}>Date</th>
                    <th className={"px-2 py-2 text-sm text-textGrey"}>Type</th>
                    <th className={"px-2 py-2 text-sm text-textGrey"}>Amount</th>
                    <th className={"px-2 py-2 text-sm text-textGrey"}>Status</th>
                    <th className={"px-2 py-2 text-sm text-textGrey"}>Detail</th>
                </tr>
                </thead>
                <tbody>
                {transactions.map(tx => (
                    <tr key={tx._id} className={"border-t"}>
                        <td className={"px-2 py-2 text-sm"}>{formatTxDate(tx.createdAt)}</td>
                        <td className={"px-2 py-2 text-sm"}>{TX_TYPE_LABELS[tx.type] || tx.type}</td>
                        <td className={`px-2 py-2 text-sm ${Number(tx.amount) < 0 ? 'text-red-600' : ''}`}>
                            {Number(tx.amount) >= 0 ? '+' : ''}{formatBalance(tx.amount)}
                        </td>
                        <td className={"px-2 py-2 text-sm"}>{tx.status}</td>
                        <td className={"px-2 py-2 text-sm"}>
                            {tx.WalletPackage?.name || ''}
                            {tx.ExperienceBooking ? 'Class booking' : ''}
                            {tx.ServiceBooking ? 'Service booking' : ''}
                            {tx.Booking ? 'Session booking' : ''}
                            {tx.note ? tx.note : ''}
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

const User = () => {
    const { id } = useParams();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [activationLoading, setActivationLoading] = useState(false);
    const [activationError, setActivationError] = useState(false);
    const [pendingWalletTxns, setPendingWalletTxns] = useState([]);
    const [completingTxnId, setCompletingTxnId] = useState(null);
    const [txnResults, setTxnResults] = useState({});
    const [adjustAmount, setAdjustAmount] = useState('');
    const [adjustTokenType, setAdjustTokenType] = useState('general');
    const [adjustNote, setAdjustNote] = useState('');
    const [adjustLoading, setAdjustLoading] = useState(false);
    const [adjustResult, setAdjustResult] = useState(null);

    const fetchUser = async () => {
        setLoading(true);
        try {
            const result = await Data.fetchUserById(id);
            setUser(result.data);
            setError(false);
        } catch (error) {
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    const fetchPendingWalletTxns = async () => {
        try {
            const result = await Data.adminFetchPendingWalletTransactions(id);
            setPendingWalletTxns(result.data || []);
        } catch (_) {
            setPendingWalletTxns([]);
        }
    };

    useEffect(async () => {
        await fetchUser();
        await fetchPendingWalletTxns();
    }, [id]);

    const completePendingWalletTxn = async (transactionId) => {
        setCompletingTxnId(transactionId);
        setTxnResults(prev => ({ ...prev, [transactionId]: null }));
        try {
            const result = await Data.adminCompleteWalletTransaction(transactionId);
            if (result.data.status === 'paid') {
                setTxnResults(prev => ({ ...prev, [transactionId]: { success: true, message: `Payment verified. New balance: £${(result.data.balance / 100).toFixed(2)}` } }));
            } else {
                setTxnResults(prev => ({ ...prev, [transactionId]: { success: false, message: result.data.message || 'Payment was not completed by the customer.' } }));
            }
            // Don't refetch the pending list here - the transaction is no longer
            // "pending" on the server either way, and refetching would immediately
            // drop this row (and its message) from view before it can be read.
            await fetchUser();
        } catch (error) {
            setTxnResults(prev => ({ ...prev, [transactionId]: { success: false, message: error?.response?.data?.message || 'Failed to verify transaction.' } }));
        } finally {
            setCompletingTxnId(null);
        }
    };

    const submitAdjustment = async () => {
        const parsed = parseFloat(adjustAmount);
        if (!adjustAmount || isNaN(parsed) || parsed === 0) return;
        setAdjustLoading(true);
        setAdjustResult(null);
        try {
            const amountInPence = Math.round(parsed * 100);
            await Data.createWalletAdjustment({
                userId: user._id,
                amount: amountInPence,
                tokenType: adjustTokenType,
                note: adjustNote.trim() || 'Manual wallet adjustment'
            });
            setAdjustResult({ success: true, message: `Adjustment of £${Math.abs(parsed).toFixed(2)} applied successfully.` });
            setAdjustAmount('');
            setAdjustNote('');
            await fetchUser();
        } catch (error) {
            setAdjustResult({ success: false, message: error?.response?.data?.message || 'Failed to apply adjustment.' });
        } finally {
            setAdjustLoading(false);
        }
    };

    const toggleActivation = async (active) => {
        try {
            setActivationLoading(true);
            setActivationError(false);
            await Data.setUserActivation(user._id, active);
            await fetchUser();
        } catch (error) {
            setActivationError(true);
        } finally {
            setActivationLoading(false);
        }
    };

    return (
        <>
            <Meta title={user ? `${user.firstName} ${user.lastName}` : "User"} />
            <Header heading={user ? `${user.firstName} ${user.lastName}` : "User"} backToLabel={"Users"} backToTo={"/admin/users"} />
            <div className={"p-3 lg:p-6 grid grid-cols-1 gap-3 lg:gap-6 w-full"}>
                {error ? <Error message={"Unable to load this user, please refresh the page."} /> : null}
                {activationError ? <Error message={"Unable to update activation status, please try again."} /> : null}

                {loading ? (
                    <Empty>
                        <p>Loading...</p>
                    </Empty>
                ) : user ? (
                    <>
                        <div className={"bg-white w-full rounded-lg border drop-shadow-sm [&>*]:py-4 [&>*]:px-6"}>
                            <div className={"my-2 flex justify-between items-center"}>
                                <p className={"text-lg"}>User Details</p>
                            </div>
                            <Field label={"First Name"} value={user.firstName} />
                            <Field label={"Last Name"} value={user.lastName} />
                            <Field label={"Email Address"} value={user.email} />
                            <Field label={"Phone Number"} value={user.phoneNumber} />
                            <Field label={"Email Verified"} value={user.emailVerified ? 'Yes' : 'No'} />
                            <Field label={"Marketing Subscribed"} value={user.marketingSubscribed ? 'Yes' : 'No'} />
                            <Field label={"Admin"} value={user.admin ? 'Yes' : 'No'} />
                            <Field label={"Status"} value={user.isActive ? 'Active' : 'Inactive'} />
                        </div>

                        <div className={"bg-white w-full rounded-lg border drop-shadow-sm [&>*]:py-4 [&>*]:px-6"}>
                            <div className={"my-2 flex justify-between items-center"}>
                                <p className={"text-lg"}>Wallet</p>
                            </div>
                            <Field label={"Wallet Balance"} value={formatBalance(user.walletBalance)} />
                            <Field label={"Class Balance"} value={formatBalance(user.classBalance)} />
                            <Field label={"Open Gym Balance"} value={formatBalance(user.openGymBalance)} />
                        </div>

                        <div className={"bg-white w-full rounded-lg border drop-shadow-sm [&>*]:py-4 [&>*]:px-6"}>
                            <div className={"my-2"}>
                                <p className={"text-lg"}>Manual Wallet Adjustment</p>
                                <p className={"text-textGrey text-sm"}>Enter a positive amount to add credit, or a negative amount to deduct. e.g. 10 to add £10, -5 to remove £5.</p>
                            </div>
                            <div className={"border-t grid grid-cols-1 gap-3 pt-4"}>
                                <div className={"grid grid-cols-3 gap-2 items-center"}>
                                    <p>Amount (£)</p>
                                    <input
                                        type={"number"}
                                        step={"0.01"}
                                        placeholder={"e.g. 10.00 or -5.00"}
                                        value={adjustAmount}
                                        onChange={(e) => setAdjustAmount(e.target.value)}
                                        className={"col-span-2 border rounded px-3 py-2 text-sm"}
                                    />
                                </div>
                                <div className={"grid grid-cols-3 gap-2 items-center"}>
                                    <p>Token Type</p>
                                    <select
                                        value={adjustTokenType}
                                        onChange={(e) => setAdjustTokenType(e.target.value)}
                                        className={"col-span-2 border rounded px-3 py-2 text-sm"}
                                    >
                                        <option value={"general"}>General (Wallet)</option>
                                        <option value={"class"}>Class</option>
                                        <option value={"open_gym"}>Open Gym</option>
                                    </select>
                                </div>
                                <div className={"grid grid-cols-3 gap-2 items-center"}>
                                    <p>Note</p>
                                    <input
                                        type={"text"}
                                        placeholder={"Reason for adjustment"}
                                        value={adjustNote}
                                        onChange={(e) => setAdjustNote(e.target.value)}
                                        className={"col-span-2 border rounded px-3 py-2 text-sm"}
                                    />
                                </div>
                                <div className={"flex items-center gap-3 pt-1"}>
                                    <button
                                        type={"button"}
                                        onClick={submitAdjustment}
                                        disabled={adjustLoading || !adjustAmount}
                                        className={"bg-primary text-white px-4 py-2 rounded-md hover:opacity-80 transition disabled:opacity-60 disabled:cursor-not-allowed text-sm"}
                                    >
                                        {adjustLoading ? "Applying..." : "Apply Adjustment"}
                                    </button>
                                    {adjustResult && (
                                        <p className={`text-sm ${adjustResult.success ? "text-green-700" : "text-red-600"}`}>
                                            {adjustResult.message}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className={"bg-white w-full rounded-lg border drop-shadow-sm [&>*]:py-4 [&>*]:px-6"}>
                            <div className={"my-2"}>
                                <p className={"text-lg"}>Wallet Transactions</p>
                                <p className={"text-textGrey text-sm"}>Full history of top-ups, spends, refunds and adjustments for this user.</p>
                            </div>
                            <div className={"border-t pt-4"}>
                                <WalletHistory userId={user._id} />
                            </div>
                        </div>

                        {pendingWalletTxns.length > 0 && (
                            <div className={"bg-white w-full rounded-lg border drop-shadow-sm [&>*]:py-4 [&>*]:px-6"}>
                                <div className={"my-2"}>
                                    <p className={"text-lg"}>Pending Wallet Payments</p>
                                    <p className={"text-textGrey text-sm"}>These wallet top-ups have not been confirmed as paid yet. Click "Verify Payment" to check with the payment provider - the balance is only credited if the provider confirms the payment actually went through.</p>
                                </div>
                                {pendingWalletTxns.map(txn => (
                                    <div key={txn._id} className={"border-t flex flex-wrap items-center justify-between gap-3"}>
                                        <div>
                                            <p>{txn.WalletPackage?.name || txn.note || 'Wallet top-up'}</p>
                                            <p className={"text-textGrey text-sm"}>Credits: £{(txn.amount / 100).toFixed(2)} &nbsp;|&nbsp; Paid: £{(txn.cashAmount / 100).toFixed(2)} &nbsp;|&nbsp; {txn.paymentProcessor || 'Unknown processor'} &nbsp;|&nbsp; {new Date(txn.createdAt).toLocaleDateString('en-GB')}</p>
                                        </div>
                                        <div className={"flex items-center gap-3"}>
                                            {txnResults[txn._id] && (
                                                <p className={`text-sm ${txnResults[txn._id].success ? 'text-green-700' : 'text-red-600'}`}>{txnResults[txn._id].message}</p>
                                            )}
                                            {!txnResults[txn._id] && (
                                                <button
                                                    type="button"
                                                    onClick={() => completePendingWalletTxn(txn._id)}
                                                    disabled={completingTxnId === txn._id}
                                                    className={"bg-yellow-400 text-yellow-900 px-3 py-1.5 rounded-md hover:opacity-80 transition disabled:opacity-60 disabled:cursor-not-allowed text-sm"}
                                                >
                                                    {completingTxnId === txn._id ? 'Verifying...' : 'Verify Payment'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className={"flex flex-wrap gap-3"}>
                            {user.isActive ? (
                                <button
                                    type="button"
                                    onClick={() => toggleActivation(false)}
                                    disabled={activationLoading}
                                    className={"bg-red-600 text-white px-3 py-2 rounded-md hover:opacity-80 transition disabled:opacity-60 disabled:cursor-not-allowed"}
                                >
                                    {activationLoading ? "Updating..." : "Deactivate User"}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => toggleActivation(true)}
                                    disabled={activationLoading}
                                    className={"bg-primary text-white px-3 py-2 rounded-md hover:opacity-80 transition disabled:opacity-60 disabled:cursor-not-allowed"}
                                >
                                    {activationLoading ? "Updating..." : "Activate User"}
                                </button>
                            )}
                        </div>
                    </>
                ) : null}
            </div>
        </>
    );
};

export default User;
